/**
 * Content keys + shape definitions for the CMS at /dashboard/content.
 * Each key here corresponds to a row in the `site_content` Supabase
 * table. Components import the seed defaults from this file and pass
 * them as the fallback to useContent(key, fallback).
 */

// ─── hero ─────────────────────────────────────────────────────────────
export interface HeroContent {
  promptUser: string;
  promptCmd: string;
  headlineLine1: string;     // e.g. "Put AI to work"
  headlineLine2: string;     // e.g. "in your business." (rendered with .hl)
  headlineLine3: string;     // e.g. "" (rendered with .dim)
  comment: string;
  ledePrefix: string;
  ledeHighlight: string;     // wrapped in .hl
  ledeSuffix: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  ghostCtaLabel: string;
  ghostCtaHref: string;
  typewriterPhrases: string[];
}

export const HERO_DEFAULT: HeroContent = {
  promptUser: '@subfocus',
  promptCmd: './deploy --what-we-do',
  headlineLine1: 'Put AI to work',
  headlineLine2: 'in your business.',
  headlineLine3: '',
  comment: '// from strategy to the build your team actually owns',
  ledePrefix: "We're a digital transformation studio in Melbourne. We help businesses adopt AI and automation in a way that ",
  ledeHighlight: 'actually sticks',
  ledeSuffix: ' — fundamentals first, AI second.',
  primaryCtaLabel: '▶ Get your AI Blueprint',
  primaryCtaHref: '/ai-readiness',
  ghostCtaLabel: 'See what we do',
  ghostCtaHref: '/services',
  typewriterPhrases: [
    'writing AI strategy',
    'building custom AI agents',
    'automating the busywork',
    'training teams to run it',
  ],
};

// ─── shared diff line (used by ops_stack_diff + work details) ─────────
export interface DiffLine {
  ln: string;          // line number tag, e.g. "@034"
  op: '+' | '-';
  txt: string;         // plain text content
  highlight?: string;  // substring within txt to wrap in <span class="hl">
}

// ─── work table (client engagements) ──────────────────────────────────
export interface MetricRow {
  label: string;
  before: string;
  after: string;
  direction?: 'up' | 'down'; // up = bigger is better, down = smaller is better
  note?: string;
}

export interface WorkDetails {
  title: string;
  headline: string;
  opsStackLabel: string;
  opsStack: DiffLine[];
  metrics: MetricRow[];
}

export interface WorkRow {
  pid: string;
  proc: string;
  cli: string;
  stat: 'live' | 'ship' | 'qa';
  cpu: string;
  href: string;
  details?: WorkDetails;
  name?: string;    // display name, e.g. "Paramount Liquor"
  sector?: string;  // e.g. "Liquor wholesale"
  logo?: string;    // public path to logo image
}

export const WORK_TABLE_DEFAULT: WorkRow[] = [
  {
    pid: '#0501',
    proc: 'paramount-liquor',
    cli: 'AI strategy, governance and Claude Enterprise rollout for the senior leadership team.',
    stat: 'live',
    cpu: '96.4%',
    href: '/work?c=paramount-liquor',
    name: 'Paramount Liquor',
    sector: 'Liquor wholesale',
    logo: '/client-logos/paramount-liquor.webp',
    details: {
      title: 'AI strategy, governance and Claude Enterprise rollout.',
      headline:
        "We ran an AI strategy workshop for the Paramount Liquor senior leadership team, scored their AI readiness, built an AI policy and governance framework, and are now rolling out Claude Enterprise across the business — including live agent-building demos with the team.",
      opsStackLabel: 'What we built for Paramount Liquor',
      opsStack: [
        { ln: '@001', op: '+', txt: 'AI strategy workshop for the senior leadership team', highlight: 'AI strategy workshop' },
        { ln: '@002', op: '+', txt: 'AI Blueprint with readiness scoring' },
        { ln: '@003', op: '+', txt: 'AI policy and governance framework', highlight: 'governance framework' },
        { ln: '@004', op: '+', txt: 'Claude Enterprise rollout across the business' },
        { ln: '@005', op: '+', txt: 'Live agent-building demos with the team' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0502',
    proc: 'the-co',
    cli: 'Email triage agent, financial dashboard, BAS checklist agent and invoice processing into Xero.',
    stat: 'live',
    cpu: '92.1%',
    href: '/work?c=the-co',
    name: 'The Co',
    sector: 'Accounting',
    logo: '/client-logos/the-co.jpg',
    details: {
      title: 'Email triage, financial dashboard and automated invoice processing.',
      headline:
        "We built an email-triage agent that sorts The Co's inbox three times a day and drafts replies, a financial dashboard showing job progress, margins and WIP, a BAS-checklist agent, and invoice processing that drops PDFs straight into Xero. Their AI readiness scored 72/100.",
      opsStackLabel: 'What we built for The Co',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Email-triage agent — sorts inbox 3x/day and drafts replies', highlight: 'email-triage agent' },
        { ln: '@002', op: '+', txt: 'Financial dashboard — job progress, margins and WIP' },
        { ln: '@003', op: '+', txt: 'BAS-checklist agent for compliance reviews' },
        { ln: '@004', op: '+', txt: 'Invoice processing — PDFs drop straight into Xero', highlight: 'invoice processing' },
        { ln: '@005', op: '+', txt: 'AI Blueprint — readiness scored 72/100' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0503',
    proc: 'cera-stribley',
    cli: 'AI render dashboard, site-analysis tool, email search agent, AI policy and CRM automation. Ongoing retainer.',
    stat: 'live',
    cpu: '91.7%',
    href: '/work?c=cera-stribley',
    name: 'Cera Stribley',
    sector: 'Architecture and design',
    logo: '/client-logos/cera-stribley.png',
    details: {
      title: "Doris, a site-analysis tool, email search over 26,000 project emails and a full AI policy.",
      headline:
        "We built Doris — an AI render dashboard that turns architectural images into animated video. A site-analysis tool that pulls planning, heritage and flood data into one report. A letter-of-offer generator. An email and legal search agent over 26,000 project emails. A full AI policy and strategy. CRM automation. We stay on as their ongoing strategy partner.",
      opsStackLabel: 'What we built for Cera Stribley',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Doris — AI render dashboard that turns images into animated video', highlight: 'Doris' },
        { ln: '@002', op: '+', txt: 'Site-analysis tool — planning, heritage and flood data in one report' },
        { ln: '@003', op: '+', txt: 'Letter-of-offer generator' },
        { ln: '@004', op: '+', txt: 'Email and legal search agent over 26,000 project emails', highlight: 'email search agent' },
        { ln: '@005', op: '+', txt: 'Full AI policy and strategy' },
        { ln: '@006', op: '+', txt: 'CRM automation' },
        { ln: '@007', op: '+', txt: 'Ongoing strategy retainer' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0504',
    proc: 'hacer',
    cli: 'AI strategy, Microsoft Copilot rollout, training program for 400+ staff and a project-overview agent.',
    stat: 'live',
    cpu: '78.3%',
    href: '/work?c=hacer',
    name: 'Hacer',
    sector: 'Construction',
    logo: '/client-logos/hacer.jpg',
    details: {
      title: 'Full AI strategy, Copilot rollout and a training program built into their LMS.',
      headline:
        "For Hacer — a construction business of 400+ people — we built the full AI strategy and policy with traffic-light governance, rolled out Microsoft Copilot, and ran a company-wide training program: champions cohort feeding into department streams, with modules built into their LMS. Plus a project-overview agent.",
      opsStackLabel: 'What we built for Hacer',
      opsStack: [
        { ln: '@001', op: '+', txt: 'AI strategy and policy with traffic-light governance', highlight: 'traffic-light governance' },
        { ln: '@002', op: '+', txt: 'Microsoft Copilot rollout across the business' },
        { ln: '@003', op: '+', txt: 'Champions cohort training feeding into department streams' },
        { ln: '@004', op: '+', txt: 'SCORM training modules built into their LMS', highlight: 'LMS' },
        { ln: '@005', op: '+', txt: 'Project-overview agent for the team' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0505',
    proc: 'jla-advisory',
    cli: 'Compliance dashboard, automated meeting notes, rebuilt website and Claude migration.',
    stat: 'live',
    cpu: '88.6%',
    href: '/work?c=jla-advisory',
    name: 'JLA',
    sector: 'Business Advisory',
    details: {
      title: 'Compliance dashboard, automated meeting notes and a rebuilt website.',
      headline:
        "We built a custom compliance-checklist dashboard the JLA team runs daily, automated their meeting notes — pulls the transcript, classifies the meeting and drafts a formatted email — rebuilt their one-page website, and migrated the team onto Claude.",
      opsStackLabel: 'What we built for JLA',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Custom compliance-checklist dashboard — used daily', highlight: 'compliance dashboard' },
        { ln: '@002', op: '+', txt: 'Automated meeting notes — transcript, classification and formatted email draft' },
        { ln: '@003', op: '+', txt: 'Rebuilt one-page website' },
        { ln: '@004', op: '+', txt: 'Team migrated onto Claude' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0506',
    proc: 'ncl',
    cli: 'Meeting and follow-up agents for legal practitioners. Secure chat over client matters.',
    stat: 'live',
    cpu: '83.4%',
    href: '/work?c=ncl',
    name: 'NCL',
    sector: 'Legal',
    logo: '/client-logos/ncl.png',
    details: {
      title: 'AI agents for legal practitioners and secure chat over client matters.',
      headline:
        "For NCL we built AI agents that help legal practitioners with meetings and follow-ups, and a secure chat agent that answers questions and looks up client matters — without sensitive data leaving their systems.",
      opsStackLabel: 'What we built for NCL',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Meeting and follow-up agents for legal practitioners', highlight: 'legal practitioners' },
        { ln: '@002', op: '+', txt: 'Secure chat agent over client matters — data stays on-premises', highlight: 'secure chat' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0508',
    proc: 'bas-consulting',
    cli: 'AI agents for new client leads, back-office admin and quoting and invoicing.',
    stat: 'ship',
    cpu: '74.2%',
    href: '/work?c=bas-consulting',
    name: 'BAS Consulting',
    sector: 'Accounting and finance',
    logo: '/client-logos/bas-consulting.webp',
    details: {
      title: 'AI agents handling new leads, back-office admin and invoicing.',
      headline:
        "For BAS Consulting we deployed AI agents that handle new client leads, automate back-office admin, and help with quoting and invoicing.",
      opsStackLabel: 'What we built for BAS Consulting',
      opsStack: [
        { ln: '@001', op: '+', txt: 'AI agent for new client lead intake', highlight: 'lead intake' },
        { ln: '@002', op: '+', txt: 'Back-office admin automation' },
        { ln: '@003', op: '+', txt: 'Quoting and invoicing assistance' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0509',
    proc: 'arthur-contracting',
    cli: 'Replaced manual payroll and invoicing with an AI-driven workflow and a custom dashboard.',
    stat: 'ship',
    cpu: '69.5%',
    href: '/work?c=arthur-contracting',
    name: 'Arthur Contracting',
    sector: 'Electrical contracting',
    logo: '/client-logos/arthur-contracting.png',
    details: {
      title: 'Payroll and invoicing on autopilot. A dashboard the team actually opens.',
      headline:
        "We replaced Arthur Contracting's manual payroll and invoicing with an AI-driven workflow that reads rate cards and applies billing rules to dockets in bulk — and built a custom dashboard the team uses daily.",
      opsStackLabel: 'What we built for Arthur Contracting',
      opsStack: [
        { ln: '@001', op: '+', txt: 'AI-driven payroll workflow — reads rate cards and applies billing rules to dockets', highlight: 'payroll workflow' },
        { ln: '@002', op: '+', txt: 'Automated invoicing — bulk docket processing', highlight: 'automated invoicing' },
        { ln: '@003', op: '+', txt: 'Custom dashboard — team visibility into jobs and billing' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0510',
    proc: 'go-protect',
    cli: 'Automated the sales process, commission management and partner revenue payments.',
    stat: 'live',
    cpu: '—',
    href: '/work?c=go-protect',
    name: 'Go Protect',
    sector: 'Insurance',
    logo: '/client-logos/go-protect.png',
    details: {
      title: 'Sales, commissions and partner payments — automated end to end.',
      headline:
        "For Go Protect we automated the sales process, commission management, and the partner revenue payment systems behind it — taking the manual work out of how deals are sold, tracked and paid.",
      opsStackLabel: 'What we built for Go Protect',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Automated sales process', highlight: 'sales process' },
        { ln: '@002', op: '+', txt: 'Commission management automation', highlight: 'commission management' },
        { ln: '@003', op: '+', txt: 'Partner revenue payment systems', highlight: 'partner revenue' },
      ],
      metrics: [],
    },
  },
  {
    pid: '#0511',
    proc: 'tal',
    cli: 'Facilitated education frameworks across the TAL Risk Academy as a national rollout.',
    stat: 'live',
    cpu: '—',
    href: '/work?c=tal',
    name: 'TAL',
    sector: 'Life insurance',
    logo: '/client-logos/tal.avif',
    details: {
      title: 'Education frameworks for the TAL Risk Academy — rolled out nationally.',
      headline:
        "We facilitated the education frameworks inside the TAL Risk Academy and rolled them out nationally — building real AI capability across the business.",
      opsStackLabel: 'What we built for TAL',
      opsStack: [
        { ln: '@001', op: '+', txt: 'Education frameworks for the TAL Risk Academy', highlight: 'TAL Risk Academy' },
        { ln: '@002', op: '+', txt: 'National rollout across the business', highlight: 'national rollout' },
      ],
      metrics: [],
    },
  },
];

// ─── services grid ────────────────────────────────────────────────────
export interface ServiceDetails {
  longDesc: string;
  bullets: string[];
  deliverables: string[];
}

export interface ServiceCard {
  slug: string;
  manName: string;
  index: string;
  title: string;
  desc: string; // plain text — no inline HTML; rendered as a single string
  cmd: string;
  href?: string;
  sprite?: string;
  details?: ServiceDetails;
}

export const SERVICES_GRID_DEFAULT: ServiceCard[] = [
  {
    slug: 'advisory',
    manName: 'sf-advisory(1)',
    index: '01 · man',
    title: 'Advisory',
    desc: 'Where AI fits, what\'s worth doing, and what to leave alone. We build your AI strategy, policy and readiness so you move with a plan — not hype.',
    cmd: 'sf advisory',
    sprite: 'svc-strategy',
    details: {
      longDesc:
        "We sit with your leadership team, map your processes and data, and tell you exactly where AI will earn its keep. The output is a prioritised roadmap with named owners and an AI policy and governance framework that holds up to a board, your clients, and your regulator. We've run this for Paramount Liquor, Hacer, and Cera Stribley — from exec workshops to traffic-light governance frameworks and board-ready AI policy.",
      bullets: [
        'Executive workshops and leadership alignment sessions',
        'AI policy and governance framework (board-ready)',
        'AI Blueprint with readiness scoring',
        'Prioritised roadmap with named owners',
      ],
      deliverables: [
        'Strategy document (Notion or PDF)',
        'AI policy and governance framework',
        'Prioritised roadmap with owners and sequencing',
        'AI Blueprint report',
      ],
    },
  },
  {
    slug: 'automate',
    manName: 'sf-automate(1)',
    index: '02 · man',
    title: 'Automation',
    desc: 'We take out the repetitive, manual work. Connect your tools, automate the busywork, and give your team back the hours that matter.',
    cmd: 'sf automate',
    sprite: 'svc-automations',
    details: {
      longDesc:
        "We design and build automations that run on the tools your team already uses — Airtable, HubSpot, Slack, Notion, Xero — using n8n, Make, or Zapier. No vendor lock-in. Everything lives in your accounts and your team owns it from day one. We've automated payroll and invoicing for Arthur Contracting, back-office admin and lead intake for BAS Consulting, and meeting-note generation for JLA Advisory.",
      bullets: [
        'Automation built on your existing tools — no platform lock-in',
        'Workflows live in your accounts from day one',
        'Self-hosted n8n option for sensitive data',
        'Custom dashboards where the team needs visibility',
      ],
      deliverables: [
        'Working automations in your stack',
        'Runbook and handover documentation per workflow',
        'Monitoring and retry policies',
      ],
    },
  },
  {
    slug: 'agents',
    manName: 'sf-agents(1)',
    index: '03 · man',
    title: 'Agents',
    desc: 'Custom AI agents that do real jobs — triaging the inbox, handling data, running reports, and supporting your team day to day.',
    cmd: 'sf agents',
    sprite: 'svc-agents',
    details: {
      longDesc:
        "We build custom AI agents for specific jobs in your business — not general-purpose chatbots. An email-triage agent that sorts the inbox and drafts replies (The Co), a secure chat agent that answers questions over client matters without sensitive data leaving the building (NCL), a render dashboard that turns architectural images into video (Cera Stribley's Doris), and when off-the-shelf won't do, full custom apps — like a secure web and mobile app with AI document reading, adviser dashboards, and gap analysis across a whole client base.",
      bullets: [
        'Agents built for a specific job — not general-purpose',
        'Works with your existing data and tools',
        'Secure — data stays in your environment where needed',
        'Full custom apps when no off-the-shelf answer exists',
      ],
      deliverables: [
        'Working agent or app in your accounts',
        'Source code, documentation and handover',
        '30-day stabilisation period included',
      ],
    },
  },
  {
    slug: 'education',
    manName: 'sf-education(1)',
    index: '04 · man',
    title: 'Education',
    desc: 'Champion-based training that builds real capability in your team, so AI adoption sticks long after we\'ve handed it over.',
    cmd: 'sf learn',
    sprite: 'svc-education',
    details: {
      longDesc:
        "We run company-wide AI training programs designed around the way your business actually works — not generic workshops. The model is champions-first: a core cohort gets deep capability, then feeds it out to department streams. We've built SCORM modules loaded into client LMSes and run programs for businesses with 400+ staff. Outcomes are measured in shipped work and capability retained, not certificates issued.",
      bullets: [
        'Champions cohort that trains the rest of the business',
        'Department-level streams tailored to how each team works',
        'SCORM modules loadable into your existing LMS',
        'Outcomes measured in shipped work, not certificates',
      ],
      deliverables: [
        'Training program design and delivery',
        'SCORM modules for your LMS (where applicable)',
        'Champions playbook and adoption resources',
      ],
    },
  },
];

// ─── stack tree (left + right cols) ───────────────────────────────────
export interface StackLeaf { name: string; tag?: string; }
export interface StackDir { dir: string; leaves: StackLeaf[]; }
export interface StackTree { left: StackDir[]; right: StackDir[]; summary: string; }

export const STACK_TREE_DEFAULT: StackTree = {
  left: [
    { dir: './intelligence/', leaves: [
      { name: 'OpenAI', tag: 'gpt-4.1, o3' },
      { name: 'Anthropic', tag: 'claude-sonnet' },
      { name: 'ElevenLabs', tag: 'voice' },
      { name: 'LangChain', tag: 'orchestration' },
    ]},
    { dir: './automation/', leaves: [
      { name: 'n8n', tag: 'self-hosted' },
      { name: 'Make', tag: 'lightweight' },
      { name: 'Zapier', tag: 'legacy glue' },
    ]},
    { dir: './ui/', leaves: [
      { name: 'Vercel', tag: 'edge' },
      { name: 'React', tag: 'vite' },
      { name: 'Retool', tag: 'internal tools' },
    ]},
  ],
  right: [
    { dir: './data/', leaves: [
      { name: 'Supabase', tag: 'primary' },
      { name: 'BigQuery', tag: 'warehouse' },
      { name: 'dbt', tag: 'transforms' },
      { name: 'Airtable', tag: 'ops' },
    ]},
    { dir: './work/', leaves: [
      { name: 'HubSpot', tag: 'crm' },
      { name: 'Slack', tag: 'interface' },
      { name: 'Notion', tag: 'docs' },
      { name: 'Linear', tag: 'delivery' },
    ]},
    { dir: './design/', leaves: [
      { name: 'Figma', tag: 'source' },
      { name: 'Framer', tag: 'motion' },
    ]},
  ],
  summary: '28 directories, 1,042 files',
};

// ─── pipe (integrations) ──────────────────────────────────────────────
export const PIPE_TOOLS_DEFAULT: string[] = [
  'OpenAI', 'Anthropic', 'n8n', 'Make', 'Zapier', 'Supabase', 'Vercel',
  'LangChain', 'Notion', 'Airtable', 'Slack', 'HubSpot', 'Retool', 'Stripe',
  'ElevenLabs', 'Figma',
];

// ─── about (principles + team) ────────────────────────────────────────
export interface PersonCard {
  name: string;
  label: string;
  title: string;
  desc: string;
}

export const ABOUT_PRINCIPLES_DEFAULT: PersonCard[] = [
  { name: 'principle.01', label: 'core', title: 'Long-term partnership', desc: 'We stay in the work, help you upskill internally, and improve over time.' },
  { name: 'principle.02', label: 'core', title: 'Momentum beats polish', desc: 'We ship fast, then refine. Getting something useful live and sharpening it beats missing the wave entirely.' },
  { name: 'principle.03', label: 'core', title: 'Always overdeliver', desc: 'The work we do should be worth more than what you pay for it.' },
  { name: 'principle.04', label: 'core', title: 'Your stack, not ours', desc: "We meet you where your systems live — within reason, of course. Slack, Airtable, Claude, ChatGPT, Copilot. We only build bespoke when the circumstances call for it." },
];

export const ABOUT_TEAM_DEFAULT: PersonCard[] = [
  { name: '@stefan', label: 'principal', title: 'Stefan D.', desc: 'Founder & principal. Strategy, governance, and systems. Sits with the executive team. Writes the blueprints and runs the engagement.' },
  { name: '@network', label: 'partner network', title: 'Vetted specialists', desc: 'A curated bench of senior engineers, designers, and ML specialists wired in per engagement. We pick who works on your account — and who doesn\'t.' },
  { name: '@selection', label: 'how we work', title: 'Bespoke, by choice', desc: 'We don\'t take every engagement. Everything is shaped around how your business actually runs — and we partner for the long term, not just the first 90 days.' },
];

// ─── audit logos ──────────────────────────────────────────────────────
export interface AuditLogo {
  key: string;
  label: string;
  src?: string; // optional URL — when set, renders the image instead of placeholder
}

export const AUDIT_LOGOS_DEFAULT: AuditLogo[] = [
  { key: 'logo-01', label: 'paramount-liquor',   src: '/client-logos/paramount-liquor.webp' },
  { key: 'logo-02', label: 'the-co',             src: '/client-logos/the-co.jpg' },
  { key: 'logo-03', label: 'cera-stribley',      src: '/client-logos/cera-stribley.png' },
  { key: 'logo-04', label: 'hacer',              src: '/client-logos/hacer.jpg' },
  { key: 'logo-05', label: 'ncl',                src: '/client-logos/ncl.png' },
  { key: 'logo-06', label: 'bas-consulting',     src: '/client-logos/bas-consulting.webp' },
  { key: 'logo-07', label: 'go-protect',         src: '/client-logos/go-protect.png' },
  { key: 'logo-08', label: 'tal',                src: '/client-logos/tal.avif' },
  { key: 'logo-09', label: 'arthur-contracting', src: '/client-logos/arthur-contracting.png' },
];

// ─── partner logos (home strip) ───────────────────────────────────────
export const PARTNER_LOGOS_DEFAULT: AuditLogo[] = [
  { key: 'claude',     label: 'Claude' },
  { key: 'chatgpt',    label: 'ChatGPT' },
  { key: 'gemini',     label: 'Gemini' },
  { key: 'elevenlabs', label: 'ElevenLabs' },
  { key: 'n8n',        label: 'n8n' },
  { key: 'perplexity', label: 'Perplexity' },
  { key: 'copilot',    label: 'Copilot' },
  { key: 'supabase',   label: 'Supabase' },
  { key: 'granola',    label: 'Granola' },
];

// ─── live log (studio.log animation) ──────────────────────────────────
export interface LogLine {
  t?: string;            // optional pre-set timestamp; if omitted (pool entries), live time is used
  s: string;             // status glyph (✓ → ⚠)
  m: string;             // message — supports inline <span class="k">…</span> pills
  cls?: 'warn' | 'err';
}

export interface LiveLogContent {
  title: string;
  seed: LogLine[];
  pool: LogLine[];
}

export const LIVE_LOG_DEFAULT: LiveLogContent = {
  title: 'studio.log — tail -f',
  seed: [
    { t: '09:14:02', s: '✓', m: 'agent.strategy <span class="k">pilot_brief</span> ready' },
    { t: '09:14:18', s: '→', m: 'sync <span class="k">airtable → supabase</span> · 1,240 rows' },
    { t: '09:15:07', s: '✓', m: 'workflow <span class="k">lead_scoring</span> deployed' },
    { t: '09:16:22', s: '→', m: 'workshop · <span class="k">Paramount Liquor</span> · 12 attendees' },
  ],
  pool: [
    { s: '✓', m: 'blueprint #842 scored <span class="k">78/100</span>' },
    { s: '→', m: 'integration <span class="k">n8n → slack</span> active' },
    { s: '✓', m: 'deploy <span class="k">v12.3</span> to production' },
    { s: '⚠', cls: 'warn', m: 'retry <span class="k">openai · rate_limit</span>' },
    { s: '→', m: 'training · <span class="k">prompt-craft 201</span> · 8 seats' },
    { s: '✓', m: 'agent.ops <span class="k">ticket_triage</span> merged' },
    { s: '→', m: 'sync <span class="k">airtable → supabase</span> · 720 rows' },
  ],
};

// ─── ops_stack diff (the "From we should do this..." component) ───────
export interface OpsStackDiffContent {
  fileLabel: string;
  lines: DiffLine[];
}

export const OPS_STACK_DIFF_DEFAULT: OpsStackDiffContent = {
  fileLabel: 'A typical first year — before and after',
  lines: [
    { ln: '@034', op: '-', txt: '12 manual spreadsheets, reconciled by hand every Monday' },
    { ln: '@035', op: '-', txt: '3 tools nobody remembers choosing' },
    { ln: '@036', op: '-', txt: '"we\'ll fix the data later" (for 18 months)' },
    { ln: '@037', op: '-', txt: 'a strategy deck from a consultant who shipped zero lines' },
    { ln: '@038', op: '+', txt: 'one automation layer running the boring parts, 24/7', highlight: 'automation layer' },
    { ln: '@039', op: '+', txt: 'three custom agents handling tickets, leads, and research', highlight: 'custom agents' },
    { ln: '@040', op: '+', txt: 'a data layer you can actually query', highlight: 'data layer' },
    { ln: '@041', op: '+', txt: 'a team that speaks fluent AI and owns the roadmap' },
  ],
};

// ─── about · our story ────────────────────────────────────────────────
export interface StoryEvent {
  date: string;
  title: string;
  description: string;
  /** Optional picture for this step of the About story. */
  image?: string;
  imageAlt?: string;
}

export interface AboutStoryContent {
  intro: string;
  imageSrc?: string;
  imageAlt?: string;
  events: StoryEvent[];
}

export const ABOUT_STORY_DEFAULT: AboutStoryContent = {
  intro:
    "Subfocus started as an internal project. The goal was to automate a single business end-to-end and run it with zero staff. It worked. What began as one experiment became a studio, and the studio became what it is now: a small Melbourne team helping businesses do the same thing we did first.",
  imageSrc: '/story.png',
  imageAlt: 'Pixel-art map of the subfocus studio and our journey — origin, pivot, the intelligence era, and today',
  events: [
    {
      date: '2020',
      title: 'origin',
      description:
        'Started as an internal project: automate a single business and run it autonomously in the advice space. The result: a 500-client insurance practice operating with zero staff.',
    },
    {
      date: '2021',
      title: 'pivot',
      description:
        'After automating The Risk Lab for subfocus founder Stefan, we pivoted to help other advice practices do the same, automating and reimagining processes to drive real efficiency.',
    },
    {
      date: '2022',
      title: 'the intelligence era',
      description:
        'The AI circus rolls into town. Autonomous processes with a human in the loop become easy to replicate, ChatGPT changes how everyone thinks, and Stefan branches out into other industries.',
    },
    {
      date: '2024',
      title: 'subfocus is born',
      description:
        'subfocus goes from internal resource to a standalone studio.',
    },
    {
      date: 'Today',
      title: 'today',
      description:
        'We work with a handful of select clients across construction, architecture, wholesale distribution, finance, accounting and insurance: strategic advice plus ongoing agentic builds. We take bespoke individual projects by application only.',
    },
  ],
};

// ─── site appearance (background and theme, chosen in admin) ─────────
export const BACKDROP_IDS = ['contours', 'dots', 'network', 'glow'] as const;
export type BackdropId = (typeof BACKDROP_IDS)[number];
export const SITE_THEMES = ['light', 'dark'] as const;
export type SiteTheme = (typeof SITE_THEMES)[number];

export interface SiteAppearanceContent {
  backdrop: BackdropId;
  theme: SiteTheme;
}

export const SITE_APPEARANCE_DEFAULT: SiteAppearanceContent = { backdrop: 'contours', theme: 'light' };

// ─── names used in the bottom bar, on the map and in the Index ───────
export interface SiteLabelsContent {
  about: string;
  services: string;
  work: string;
  projects: string;
  media: string;
  contact: string;
  aiReadiness: string;
}
export const SITE_LABELS_DEFAULT: SiteLabelsContent = { about: 'About', services: 'Services', work: 'Clients', projects: 'Projects', media: 'Media', contact: 'Contact', aiReadiness: 'AI readiness' };

// ─── which view controls visitors get ────────────────────────────────
export interface SiteControlsContent {
  enabled: boolean;
  theme: boolean;
  background: boolean;
  fullWorld: boolean;
  zoom: boolean;
  hideInterface: boolean;
}
export const SITE_CONTROLS_DEFAULT: SiteControlsContent = { enabled: true, theme: true, background: true, fullWorld: true, zoom: true, hideInterface: true };

/** Anything missing or unrecognised falls back to the default, so a bad value can never break the page. */
export function resolveAppearance(value: Partial<Record<keyof SiteAppearanceContent, unknown>> | null | undefined): SiteAppearanceContent {
  return {
    backdrop: BACKDROP_IDS.find((id) => id === value?.backdrop) ?? SITE_APPEARANCE_DEFAULT.backdrop,
    theme: SITE_THEMES.find((theme) => theme === value?.theme) ?? SITE_APPEARANCE_DEFAULT.theme,
  };
}

// ─── page visibility (toggles for whole pages and sections) ──────────
export interface PageVisibilityContent {
  // Pages
  services: boolean;
  work: boolean;
  projects: boolean;
  people: boolean;
  stack: boolean;
  about: boolean;
  contact: boolean;
  aiReadiness: boolean;
  postIndex: boolean;
  blogIndex: boolean;
  // About sub-sections
  aboutTeam: boolean;
  aboutStory: boolean;
  aboutPrinciples: boolean;
}

export const PAGE_VISIBILITY_DEFAULT: PageVisibilityContent = {
  services: true,
  work: true,
  projects: true,
  people: true,
  stack: true,
  about: true,
  contact: true,
  aiReadiness: true,
  postIndex: true,
  blogIndex: true,
  aboutTeam: false,
  aboutStory: true,
  aboutPrinciples: true,
};

// ─── budget breakdown (How we do it / strategic intelligence) ─────────
export interface HtopRow {
  lbl: string;
  base: number;
  amp: number;
  cls?: 'hot' | 'ok' | '';
}

export interface BudgetBreakdownContent {
  chip: string;
  headingPrefix: string;
  headingHl: string;
  kicker: string;
  htopTitle: string;
  rows: HtopRow[];
}

export const BUDGET_BREAKDOWN_DEFAULT: BudgetBreakdownContent = {
  chip: 'How we do it',
  headingPrefix: 'Where your budget',
  headingHl: 'actually goes.',
  kicker: "We're direct about how we spend your time and money.",
  htopTitle: 'How we spend your budget',
  rows: [
    { lbl: 'Strategy',        base: 22, amp: 2 },
    { lbl: 'Build and ship',  base: 58, amp: 5, cls: 'ok' },
    { lbl: 'Data and plumbing', base: 46, amp: 3 },
    { lbl: 'Workshops',       base: 18, amp: 2 },
    { lbl: 'Handover and docs', base: 34, amp: 2, cls: 'ok' },
    { lbl: 'Overhead',        base: 4,  amp: 1, cls: 'hot' },
  ],
};

// ─── audit page copy (AiReadinessPage) ────────────────────────────────
export interface AuditCopyContent {
  promptCmd: string;
  headingLine1: string;
  headingLine2Hl: string;
  ledePrefix: string;
  ledeHighlight: string;
  idealHeadingPrefix: string;
  idealHeadingHl: string;
  bullets: string[];
  ctaLinkLabel: string;
}

export const AUDIT_COPY_DEFAULT: AuditCopyContent = {
  promptCmd: 'sf blueprint --url=yours --free',
  headingLine1: 'Not sure where to start with AI?',
  headingLine2Hl: 'Get your free AI Blueprint',
  ledePrefix: 'A tailored AI Blueprint for your business — delivered in 24 hours.',
  ledeHighlight: "(yep, it's free).",
  idealHeadingPrefix: 'Ideal for',
  idealHeadingHl: 'businesses that:',
  bullets: [
    'Want to understand where AI can actually move the needle for them',
    'Are in the early stages of planning and want a clear starting point',
    'Are ready to map their AI opportunities before committing to anything',
  ],
  ctaLinkLabel: 'how does it work?',
};

// ─── audit modal (AiReadinessExplanationModal) ────────────────────────
export type AuditModalIcon = 'search' | 'target' | 'lightbulb' | 'trending-up';

export interface AuditBenefit {
  iconKey: AuditModalIcon;
  title: string;
  description: string;
}

export interface AuditModalContent {
  title: string;
  intro: string;
  calloutText: string;
  benefitsHeading: string;
  benefits: AuditBenefit[];
  processHeading: string;
  processSteps: string[];
  whyFreeHeading: string;
  whyFreeText: string;
}

export const AUDIT_MODAL_DEFAULT: AuditModalContent = {
  title: 'What is an AI Blueprint?',
  intro:
    "You want to know where AI fits in your business — but not sure where to begin. We research your business, map your processes and tools, and put together a bespoke AI Blueprint showing you exactly where to start and what's worth doing. You get a real document, not a generic report.",
  calloutText: "It's free, bespoke, and delivered in 24 hours",
  benefitsHeading: "What's in the Blueprint",
  benefits: [
    {
      iconKey: 'search',
      title: 'Current state snapshot',
      description:
        'We look at your existing processes, tools, and data to understand what you are working with before we recommend anything.',
    },
    {
      iconKey: 'target',
      title: 'Where AI will earn its keep',
      description:
        'Specific areas where AI can deliver real impact for your business — not theoretical wins, but things you can actually act on.',
    },
    {
      iconKey: 'lightbulb',
      title: 'A clear starting point',
      description:
        'A prioritised view of what to tackle first. The Blueprint is step one toward your full AI Playbook if you want to go deeper.',
    },
    {
      iconKey: 'trending-up',
      title: 'Honest risk flags',
      description:
        'What to watch out for before you invest — so you go in with eyes open, not assumptions.',
    },
  ],
  processHeading: 'How It Works',
  processSteps: [
    'We analyse your website and business',
    'A short conversation with Pablo, our AI assistant',
    'We produce your bespoke AI Blueprint',
    'Delivered to your inbox within 24 hours',
  ],
  whyFreeHeading: 'Why is it free?',
  whyFreeText:
    "Every business deserves to understand their AI potential before committing to anything. The Blueprint gives you a real foundation — and if you choose to work with us afterward, we already know your business inside out.",
};

// ─── contact page ─────────────────────────────────────────────────────
export interface ContactChannel {
  key: string;
  label: string;
  title: string;
  desc: string;
  cmd: string;
  action?: string;
  href?: string;
  external?: boolean;
}

export interface ContactPageContent {
  promptCmd: string;
  headlineLine1: string;
  headlineLine2Hl: string;
  lede: string;
  channels: ContactChannel[];
  formHeading: string;
  formLede: string;
  formButtonLabel: string;
}

export const CONTACT_PAGE_DEFAULT: ContactPageContent = {
  promptCmd: 'mail -s "hello" hello@subfocus.ai',
  headlineLine1: 'Say hello.',
  headlineLine2Hl: 'We read every one.',
  lede: 'No intake form gauntlet. One email reaches a founder within the business day. Or pick the channel that suits you.',
  channels: [
    {
      key: 'channel.01',
      label: 'primary',
      title: 'Email',
      desc: 'hello@subfocus.ai — reaches a founder, not a shared inbox. Median reply time: 3 hours during AEST business hours.',
      cmd: 'mail hello@subfocus.ai',
      action: '▶ open',
      href: 'mailto:hello@subfocus.ai',
      external: true,
    },
    {
      key: 'channel.02',
      label: 'social',
      title: 'Instagram',
      desc: '@subfocus.ai — the studio day-to-day: builds in progress, demos, and the occasional experiment that escapes the lab.',
      cmd: 'open instagram.com/subfocus.ai',
      action: '↗ go',
      href: 'https://www.instagram.com/subfocus.ai/',
      external: true,
    },
    {
      key: 'channel.03',
      label: 'social',
      title: 'LinkedIn',
      desc: 'We post working notes, case debriefs, and occasional takes on what actually works in AI adoption.',
      cmd: 'open linkedin.com/company/subfocus',
      action: '↗ go',
      href: 'https://linkedin.com/company/subfocusai',
      external: true,
    },
    {
      key: 'channel.04',
      label: 'irl',
      title: 'Melbourne HQ',
      desc: "Suite 87, 1085 Mt Alexander Road, Essendon VIC 3040. Happy to host a 30-min coffee-table chat if you're local and already know what you want.",
      cmd: 'cat ~/melbourne/hq.txt',
    },
  ],
  formHeading: 'Or just write.',
  formLede: "Use the box. We reply to everything that isn't clearly a cold SDR script.",
  formButtonLabel: '▶ send',
};

// ─── site nav ─────────────────────────────────────────────────────────
export interface SiteNavItem {
  to: string;
  label: string;
}

export interface SiteNavContent {
  brand: string;
  version: string;
  items: SiteNavItem[];
  ctaLabel: string;
  ctaHref: string;
}

export const SITE_NAV_DEFAULT: SiteNavContent = {
  brand: 'subfocus',
  version: 'v2.6.1',
  items: [
    { to: '/', label: 'index' },
    { to: '/about', label: 'about' },
    { to: '/services', label: 'services' },
    { to: '/work', label: 'work' },
    { to: '/contact', label: 'contact' },
  ],
  ctaLabel: '▶ AI Blueprint',
  ctaHref: '/ai-readiness',
};

// ─── site footer ──────────────────────────────────────────────────────
export interface FooterLink {
  label: string;
  to: string;
}

export interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

export interface SiteFooterContent {
  brand: string;
  tagline: string;
  address: string;
  email: string;
  columns: FooterColumn[];
  bottomLeft: string;
  bottomRight: string;
}

export const SITE_FOOTER_DEFAULT: SiteFooterContent = {
  brand: 'subfocus.ai',
  tagline: 'An AI-led digital transformation studio.',
  address: 'Melbourne, Australia.',
  email: 'hello@subfocus.ai',
  columns: [
    {
      heading: 'Services',
      links: [
        { label: 'Advisory', to: '/services/advisory' },
        { label: 'Agentic AI', to: '/services/agents' },
        { label: 'Automation', to: '/services/automation' },
        { label: 'Education', to: '/services/education' },
        { label: 'Custom projects', to: '/services/custom' },
      ],
    },
    {
      heading: 'Resources',
      links: [
        { label: 'AI Blueprint', to: '/ai-readiness' },
        { label: 'Clients', to: '/work' },
        { label: 'Projects', to: '/projects' },
        { label: 'Insights', to: '/blog/insights' },
      ],
    },
    {
      heading: 'Social',
      links: [
        { label: 'Instagram', to: 'https://www.instagram.com/subfocus.ai/' },
        { label: 'Blog', to: '/blog/insights' },
        { label: 'LinkedIn', to: 'https://linkedin.com/company/subfocusai' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Privacy', to: '/privacy' },
        { label: 'Terms', to: '/terms' },
        { label: 'Dashboard', to: '/dashboard' },
      ],
    },
  ],
  bottomLeft: '© 2026 · built in Melbourne',
  bottomRight: 'v2.6.1 · edge · AU-SE1',
};

// ─── seo meta ─────────────────────────────────────────────────────────
export interface SeoRouteMeta {
  title: string;
  description?: string;
  ogImage?: string;
}

export interface SeoMetaContent {
  routes: Record<string, SeoRouteMeta>;
}

export const SEO_META_DEFAULT: SeoMetaContent = {
  routes: {
    '/': {
      title: 'subfocus.ai | AI Strategy & Build Studio',
      description: 'We help smart businesses figure out where AI fits, then bring it to life. An AI strategy and build studio in Melbourne.',
    },
    '/about': {
      title: 'About | subfocus.ai',
      description: 'Who we are and how we got here. An AI strategy and build studio in Melbourne.',
    },
    '/services': {
      title: 'Services | subfocus.ai',
      description: 'Advisory, agentic AI, automation, education and custom projects. Practical ways to make AI part of everyday work.',
    },
    '/work': {
      title: 'Clients | subfocus.ai',
      description: 'Client stories, floor by floor. See what we build, connect and make possible.',
    },
    '/projects': {
      title: 'Projects | subfocus.ai',
      description: 'Products of our own, and platforms we built for clients. Software shaped around the people who use it.',
    },
    '/media': {
      title: 'Media | subfocus.ai',
      description: 'Deep dives into AI, automation and the machinery behind shipping it.',
    },
    '/contact': {
      title: 'Contact | subfocus.ai',
      description: 'Tell us what you are working on. We will tell you where AI can help, and where it can not.',
    },
    '/ai-readiness': {
      title: 'AI readiness | subfocus.ai',
      description: 'A clearer read on where AI fits in your business.',
    },
    '/dashboard': {
      title: 'subfocus.ai — dashboard',
      description: 'Internal hub for the subfocus team.',
    },
    '/privacy': {
      title: 'Privacy Policy — subfocus.ai',
      description: 'How Subfocus.ai collects, uses, and protects your personal information.',
    },
    '/terms': {
      title: 'Terms of Service — subfocus.ai',
      description: 'Terms governing your use of the Subfocus.ai website and services.',
    },
  },
};

// ─── dashboard cards ──────────────────────────────────────────────────
export interface DashCard {
  key: string;
  label: string;
  title: string;
  desc: string;
  cmd: string;
  action: string;
  href: string;
  external?: boolean;
}

export interface DashboardCardsContent {
  cards: DashCard[];
}

export const DASHBOARD_CARDS_DEFAULT: DashboardCardsContent = {
  cards: [
    {
      key: 'login.client',
      label: 'app',
      title: 'Client login',
      desc: 'Customer-facing dashboard. Sign in with your subfocus.ai credentials to access reports, agents, and ops dashboards.',
      cmd: 'open dash.subfocus.ai',
      action: '↗ open',
      href: 'https://dash.subfocus.ai',
      external: true,
    },
    {
      key: 'audits.admin',
      label: 'admin',
      title: 'Readiness Audits',
      desc: 'Manage incoming AI readiness audits — pipeline runs, prompts, audit detail review, manual creation, and bulk controls.',
      cmd: 'cd /admin/dashboard',
      action: '▶ enter',
      href: '/admin/dashboard',
    },
    {
      key: 'workspace.proposals',
      label: 'workspace',
      title: 'Workspace',
      desc: 'Proposal builder and engagement workspace for active client work.',
      cmd: 'open proposals.subfocus.ai',
      action: '↗ open',
      href: 'https://proposals.subfocus.ai',
      external: true,
    },
    {
      key: 'content.editor',
      label: 'cms',
      title: 'Content editor',
      desc: 'Edit live site content (work table, hero, services, stack, integrations, team, audit logos) without a deploy.',
      cmd: 'sf cms --edit',
      action: '✎ edit',
      href: '/dashboard/content',
    },
  ],
};

// ─── legal pages (markdown body) ─────────────────────────────────────
export interface LegalPageContent {
  title: string;
  lastUpdated: string;
  markdown: string;
}

export const LEGAL_PRIVACY_DEFAULT: LegalPageContent = {
  title: 'Privacy Policy',
  lastUpdated: '15 April 2026',
  markdown: `At Subfocus.ai, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our services, including the Subfocus AI Dashboard and the Inbox Agent module.

## Information We Collect

- Contact information (name, email address, phone number)
- Business information (company name, industry, role)
- Communication preferences and interaction history
- Technical information (IP address, browser type, device information)
- Usage data and analytics to improve our services

## Google User Data

When you connect your Gmail account to the Inbox Agent module, Subfocus.ai accesses and processes specific data from your Google account to deliver the service:

- **Your Gmail messages** — read to classify, summarise, and draft replies on your behalf
- **Gmail labels** — read and created/updated so the agent can file messages into the categories you configure
- **Your Google profile** (email address, display name, account ID) — used only to identify your account within our dashboard

This data is used **solely to provide the Inbox Agent service you signed up for**. We do not:

- Use your Google user data to train, fine-tune, or improve any machine-learning or AI models, generalised or otherwise
- Sell, trade, or rent your Google user data to any third party
- Use your Google user data to serve advertisements
- Allow humans to read the contents of your Gmail messages, except with your explicit consent for support purposes, or where required by law

Gmail message contents are processed in memory for the duration of each agent run. We retain only metadata (subject line, sender, category, priority, action taken) and the AI-generated digest in your dashboard's run history. You can request deletion of all run history at any time by emailing hello@subfocus.ai.

## Limited Use Disclosure

Subfocus.ai's use and transfer of information received from Google APIs to any other app will adhere to the [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the **Limited Use** requirements.

## Microsoft User Data

When you connect a Microsoft 365 account, Subfocus.ai accesses your Outlook mail and calendar metadata through the Microsoft Graph API under the permissions you explicitly grant during consent. The same principles in the "Google User Data" section apply: your mail content is used only to deliver the Inbox Agent service, is not used for model training, is not sold, and is retained only as metadata plus digests in your dashboard.

## How We Use Your Information

- To provide and improve our services, including the AI Dashboard and Inbox Agent
- To communicate with you about our services, updates, and support matters
- To personalise your experience and recommendations
- To analyse aggregated, non-identifying usage patterns and optimise our platform
- To comply with legal obligations and protect our rights

## Data Sharing and Disclosure

We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

- With your explicit consent
- With trusted infrastructure and service providers (e.g. Supabase for hosting and database, Anthropic for AI processing, Resend for transactional email) strictly to operate the service on our behalf, under contractual obligations that prohibit secondary use
- When required by law or to protect our legal rights
- In connection with a business transfer or acquisition, with prior notice to you

## Data Security

We implement commercially reasonable security measures to protect your personal information, including encryption of data in transit (TLS) and at rest (AES-256-GCM for third-party OAuth tokens), access controls on our databases, and service-role isolation between client tenants. No method of transmission or storage over the internet is 100% secure; we will notify you promptly in the event of a data breach affecting your account, in line with our obligations under the Australian Privacy Act 1988.

## Your Rights

Subject to applicable law, you have the right to:

- Access and review your personal information
- Request corrections to inaccurate or incomplete data
- Request deletion of all data we hold about you, including your Google or Microsoft account connection, processed email metadata, and AI-generated digests. We will complete deletion within 30 days of a verified request sent to hello@subfocus.ai.
- Opt out of marketing communications at any time
- Port your data to another service, where technically feasible
- Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you believe your privacy rights have been breached

## Cookies and Tracking

We use cookies and similar technologies strictly to maintain your authenticated session, remember interface preferences (such as dark mode), and produce aggregated usage analytics. We do not sell cookie data or use it for cross-site advertising. You can control cookie settings through your browser preferences, but disabling session cookies will prevent sign-in.

## Data Location

Your data is stored on infrastructure hosted in Supabase's regional data centres (currently AWS ap-southeast-2, Sydney). Some AI processing occurs on servers operated by Anthropic in the United States; only the text strictly required for each operation is transmitted and is not retained by the processor beyond the inference request.

## Children's Privacy

Subfocus.ai is a business service and is not intended for use by children under 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us at hello@subfocus.ai and we will delete it.

## International Users

If you access Subfocus.ai from outside Australia, you acknowledge that your information may be transferred to, stored in, and processed in Australia and the other jurisdictions described above.

## Updates to This Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page, updating the "Last updated" date, and for significant changes, by email to your registered address.

## Contact Us

If you have any questions about this Privacy Policy, wish to exercise any of your rights above, or have concerns about our data practices, please contact us at:

**Email:** hello@subfocus.ai

**Postal:** Subfocus.ai, PO Box 87, Essendon North VIC 3041, Australia`,
};

export const LEGAL_TERMS_DEFAULT: LegalPageContent = {
  title: 'Terms of Service',
  lastUpdated: '15 April 2026',
  markdown: `These Terms of Service ("Terms") govern your access to and use of the Subfocus.ai website, the Subfocus AI Dashboard, the Inbox Agent module, and any related services (together, the "Service") operated by Subfocus.ai ("we", "us", "our"). By accessing or using the Service you agree to be bound by these Terms. If you do not agree, do not use the Service.

## 1. Eligibility

The Service is intended for business use by individuals aged 16 or over. By using the Service you represent that you meet this age threshold and, where you are using the Service on behalf of an organisation, that you have authority to bind that organisation to these Terms.

## 2. Your Account

Some features require you to sign in. You are responsible for:

- Maintaining the confidentiality of your sign-in credentials
- All activity that occurs under your account
- Keeping your contact information accurate so we can reach you about security or service matters
- Notifying us promptly at hello@subfocus.ai if you suspect any unauthorised use of your account

## 3. Third-Party Account Connections

Parts of the Service (notably the Inbox Agent) work by connecting to third-party accounts that you control, such as Gmail or Microsoft 365, through their official OAuth consent flows. When you connect such an account you explicitly authorise Subfocus.ai to access the scopes shown on the provider's consent screen for the sole purpose of delivering the Service to you. You can revoke that authorisation at any time from the Service's connections screen or directly in the third-party provider's account settings. Our handling of data obtained via these connections is governed by our [Privacy Policy](/privacy).

## 4. Acceptable Use

You agree not to:

- Use the Service for any unlawful, fraudulent, or harmful purpose
- Attempt to gain unauthorised access to the Service or any other user's account or data
- Reverse-engineer, decompile, or attempt to extract source code from the Service except to the extent this restriction is prohibited by applicable law
- Scrape, spider, or otherwise harvest data from the Service in volumes or at rates likely to impair its operation for other users
- Use the Service to send unsolicited commercial messages, phishing, or other spam
- Upload, process, or transmit content that infringes any intellectual-property, privacy, or other right of a third party
- Interfere with the security or integrity of the Service or the infrastructure on which it runs

## 5. AI-Generated Content

The Service uses large language models (including models provided by Anthropic and other AI vendors) to produce summaries, classifications, and draft replies based on content you provide or connect. You acknowledge that:

- AI outputs can contain errors, omissions, or inaccuracies. You are responsible for reviewing and approving any AI-generated output before acting on it (including, critically, before sending any draft reply).
- The Service does not send email on your behalf automatically — drafts are created in your own mailbox's drafts folder and must be sent by you manually.
- AI-generated output may be similar to output produced for other users; we make no warranty of uniqueness.

## 6. Your Content

You retain all rights in the content you upload, connect, or otherwise submit to the Service ("Your Content"). You grant us a limited, non-exclusive, worldwide, royalty-free licence to host, store, process, and transmit Your Content strictly for the purpose of providing the Service to you. We will not use Your Content to train, fine-tune, or improve any machine-learning or AI models, generalised or otherwise, and we will not sell or share Your Content with third parties except as described in our Privacy Policy.

## 7. Our Intellectual Property

The Service, including the Subfocus AI Dashboard, the Inbox Agent engine, system prompts, designs, source code, and branding, is owned by Subfocus.ai and its licensors. No provision of these Terms grants you any right in our intellectual property beyond the limited right to use the Service as intended.

## 8. Fees & Billing

Paid features of the Service, where offered, are billed in accordance with the plan you select at sign-up or as agreed in a separate order form. Fees are non-refundable except where required by law or explicitly stated at the time of purchase. We may change our pricing with reasonable prior notice to you.

## 9. Service Availability

We aim to keep the Service available and operating reliably, but we do not guarantee uninterrupted access. The Service depends on third-party infrastructure (including Supabase, Anthropic, and the OAuth providers you connect) that we do not control. Scheduled maintenance, upstream incidents, or force-majeure events may cause temporary unavailability.

## 10. Disclaimers

To the fullest extent permitted by law, and except for any rights, guarantees, or remedies that cannot be excluded under the Australian Consumer Law, the Service is provided "as is" and "as available", without warranties of any kind, either express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, or error-free operation. Nothing in these Terms excludes, restricts, or modifies any non-excludable consumer guarantee.

## 11. Limitation of Liability

To the fullest extent permitted by law, Subfocus.ai will not be liable for any indirect, incidental, special, consequential, or exemplary damages, or for any loss of profits, revenue, data, business opportunity, or goodwill, arising out of or in connection with the Service. Our total aggregate liability for all claims arising from or relating to the Service will not exceed the greater of (a) the fees you paid to us in the 12 months preceding the claim, or (b) AUD $100.

## 12. Indemnity

You agree to indemnify and hold Subfocus.ai harmless from any claim, loss, or liability (including reasonable legal fees) arising from your misuse of the Service, your breach of these Terms, or your infringement of any third-party right.

## 13. Suspension & Termination

We may suspend or terminate your access to the Service at any time, with or without notice, if you breach these Terms, if required by law, or if continued provision of the Service to you poses a security or operational risk. You may terminate your account at any time by emailing hello@subfocus.ai. Upon termination, your right to use the Service ends and we will handle deletion of your data in accordance with our Privacy Policy.

## 14. Changes to the Service

We may modify, add, or remove features of the Service from time to time, generally without notice. We will provide reasonable prior notice before discontinuing the Service entirely or making any change that materially and adversely affects your use of the Service.

## 15. Changes to These Terms

We may update these Terms from time to time. We will post the updated Terms on this page and update the "Last updated" date. For material changes, we will give you reasonable prior notice by email or in-app notification. Your continued use of the Service after the effective date of the updated Terms constitutes your acceptance of them.

## 16. Governing Law & Jurisdiction

These Terms are governed by the laws of the State of Victoria, Australia. The parties submit to the exclusive jurisdiction of the courts of Victoria, Australia and the federal courts of Australia for any dispute arising out of or in connection with these Terms.

## 17. Contact

Questions about these Terms:

**Email:** hello@subfocus.ai

**Postal:** Subfocus.ai, PO Box 87, Essendon North VIC 3041, Australia`,
};

// ─── content keys registry ────────────────────────────────────────────
export const CONTENT_KEYS = {
  hero: 'hero',
  workTable: 'work_table',
  servicesGrid: 'services_grid',
  stackTree: 'stack_tree',
  pipeTools: 'pipe_tools',
  aboutPrinciples: 'about_principles',
  aboutTeam: 'about_team',
  auditLogos: 'audit_logos',
  partnerLogos: 'partner_logos',
  liveLog: 'live_log',
  opsStackDiff: 'ops_stack_diff',
  aboutStory: 'about_story',
  pageVisibility: 'page_visibility',
  budgetBreakdown: 'budget_breakdown',
  auditCopy: 'audit_copy',
  auditModal: 'audit_modal',
  contactPage: 'contact_page',
  siteNav: 'site_nav',
  siteFooter: 'site_footer',
  seoMeta: 'seo_meta',
  dashboardCards: 'dashboard_cards',
  legalPrivacy: 'legal_privacy',
  legalTerms: 'legal_terms',
  worldClients: 'world_clients',
  worldServices: 'world_services',
  worldProducts: 'world_products',
  worldPeople: 'world_people',
  worldTour: 'world_tour',
  worldAbout: 'world_about',
  siteAppearance: 'site_appearance',
  siteLabels: 'site_labels',
  siteControls: 'site_controls',
  worldCopy: 'world_copy',
} as const;
